import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Car } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import CarParametersSection from './CarParametersSection';
import LoanParametersSection from './LoanParametersSection';
import InsuranceSection from './InsuranceSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import TradeInSection from './TradeInSection';
import EarlyRepaymentSection from './EarlyRepaymentSection';
import AdditionalFeesSection from './AdditionalFeesSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета автокредита
 */
const CalculationForm = ({
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  carTypes,
  insuranceTypes,
  earlyRepaymentPeriods,
  saveAutoLoan
}) => {
  // Состояние для имени сохраняемого кредита
  const [saveLoanName, setSaveLoanName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);

  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };

  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Автоматическое обновление связанных полей
      if (name === 'carType') {
        const selectedCarType = carTypes.find(type => type.id === fieldValue);
        if (selectedCarType) {
          const currentRate = parseFloatOrDefault(prev.interestRate, 0);
          const isRateDefault = carTypes.some(ct => currentRate === ct.defaultRate);
          
          if (isRateDefault || !prev.interestRate || currentRate <= 0) {
            newState.interestRate = selectedCarType.defaultRate.toString();
          }
          
          // Сброс возраста автомобиля для нового авто
          if (fieldValue === 'new') {
            newState.carAge = '0';
          } else if (prev.carAge === '0') {
            newState.carAge = '3';
          }
        }
      }

      // Синхронизация первоначального взноса в процентах и в абсолютном значении
      if (name === 'downPayment') {
        const carPrice = parseFloatOrDefault(prev.carPrice, 0);
        if (carPrice > 0) {
          const downPaymentValue = parseFloatOrDefault(fieldValue, 0);
          const percentValue = (downPaymentValue / carPrice) * 100;
          newState.downPaymentPercent = percentValue.toFixed(2);
        }
      } else if (name === 'downPaymentPercent') {
        const carPrice = parseFloatOrDefault(prev.carPrice, 0);
        if (carPrice > 0) {
          const percentValue = parseFloatOrDefault(fieldValue, 0);
          const downPaymentValue = (carPrice * percentValue) / 100;
          newState.downPayment = downPaymentValue.toFixed(0);
        }
      } else if (name === 'carPrice') {
        // При изменении цены автомобиля пересчитываем первоначальный взнос по проценту
        const carPrice = parseFloatOrDefault(fieldValue, 0);
        if (carPrice > 0) {
          const percentValue = parseFloatOrDefault(prev.downPaymentPercent, 0);
          const downPaymentValue = (carPrice * percentValue) / 100;
          newState.downPayment = downPaymentValue.toFixed(0);
        }
      }

      // Сброс связанных полей если функция отключена
      if (name === 'includeInsurance' && !checked) {
        newState.insuranceType = 'basic';
        newState.insuranceCost = '0';
      }

      if (name === 'includeTradeIn' && !checked) {
        newState.tradeInValue = '0';
      }

      if (name === 'includeEarlyRepayment' && !checked) {
        newState.earlyRepaymentType = 'reduction';
        newState.earlyRepaymentAmount = '100000';
        newState.earlyRepaymentPeriod = 'quarterly';
        newState.earlyRepaymentStartMonth = '12';
      }

      if (name === 'includeAdditionalFees' && !checked) {
        newState.registrationFee = '0';
        newState.additionalServices = '0';
      }

      // Автоматический расчет страховки при изменении типа или цены автомобиля
      if ((name === 'carPrice' || name === 'carType' || name === 'carAge') && prev.includeInsurance) {
        const carPrice = name === 'carPrice' ? parseFloatOrDefault(fieldValue, 0) : parseFloatOrDefault(prev.carPrice, 0);
        const carType = name === 'carType' ? fieldValue : prev.carType;
        const carAge = name === 'carAge' ? parseFloatOrDefault(fieldValue, 0) : parseFloatOrDefault(prev.carAge, 0);
        
        // Рассчитываем примерную стоимость полной страховки (КАСКО + ОСАГО)
        let insuranceEstimate = 0;
        
        if (prev.insuranceType === 'full') {
          // Упрощенный алгоритм расчета КАСКО: ~5-8% от стоимости нового автомобиля, для б/у больше
          const kaskoPercent = carType === 'new' ? 5 : 5 + carAge;
          const kasko = (carPrice * kaskoPercent) / 100;
          
          // Базовая стоимость ОСАГО
          const osago = 5000 + (carType === 'used' ? carAge * 500 : 0);
          
          insuranceEstimate = Math.round(kasko + osago);
        } else {
          // Только ОСАГО
          insuranceEstimate = 5000 + (carType === 'used' ? carAge * 500 : 0);
        }
        
        newState.insuranceCost = insuranceEstimate.toString();
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [carTypes, errors, setErrors, setFormData, insuranceTypes]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      carPrice, downPayment, loanTerm, interestRate, startDate, carType, carAge,
      includeInsurance, insuranceCost, insuranceType,
      includeTradeIn, tradeInValue,
      includeEarlyRepayment, earlyRepaymentAmount, earlyRepaymentStartMonth, earlyRepaymentPeriod, earlyRepaymentType,
      includeAdditionalFees, registrationFee, additionalServices, paymentType
    } = formData;

    // Парсинг числовых значений
    const numCarPrice = parseFloatOrDefault(carPrice, -1);
    const numDownPayment = parseFloatOrDefault(downPayment, -1);
    const numLoanTermYears = parseFloatOrDefault(loanTerm, -1);
    const numLoanTermMonths = Math.round(numLoanTermYears * 12);
    const numInterestRate = parseFloatOrDefault(interestRate, -1);
    const numCarAge = parseFloatOrDefault(carAge, -1);

    // Валидация параметров автомобиля
    if (numCarPrice <= 0) newErrors.carPrice = 'Стоимость должна быть положительной.';
    if (!newErrors.carPrice && numCarPrice > 50000000) newErrors.carPrice = 'Максимальная стоимость 50 млн ₽.';

    if (numDownPayment < 0) newErrors.downPayment = 'Первоначальный взнос не может быть отрицательным.';
    if (!newErrors.downPayment && numDownPayment >= numCarPrice) {
      newErrors.downPayment = 'Первоначальный взнос не может быть больше или равен стоимости автомобиля.';
    }

    // Валидация параметров кредита
    if (numLoanTermYears <= 0) newErrors.loanTerm = 'Срок должен быть положительным.';
    if (!newErrors.loanTerm && numLoanTermYears > 10) newErrors.loanTerm = 'Максимальный срок автокредита 10 лет.';
    if (!newErrors.loanTerm && numLoanTermMonths <= 0) newErrors.loanTerm = 'Срок должен быть не менее 1 месяца.';

    if (numInterestRate <= 0) newErrors.interestRate = 'Ставка должна быть положительной.';
    if (!newErrors.interestRate && numInterestRate > 30) newErrors.interestRate = 'Ставка не может быть более 30%.';

    // Проверка даты начала кредита
    if (!startDate) newErrors.startDate = 'Укажите дату начала кредита.';
    try {
      const dateObject = new Date(startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    // Валидация типа и возраста автомобиля
    if (!carType || !['new', 'used'].includes(carType)) {
      newErrors.carType = "Выберите тип автомобиля.";
    }

    if (carType === 'used') {
      if (numCarAge <= 0) newErrors.carAge = 'Возраст должен быть положительным.';
      if (!newErrors.carAge && numCarAge > 20) newErrors.carAge = 'Максимальный возраст 20 лет.';
    }

    // Валидация страховки
    if (includeInsurance) {
      const numInsuranceCost = parseFloatOrDefault(insuranceCost, -1);
      if (numInsuranceCost < 0) newErrors.insuranceCost = 'Стоимость страховки не может быть отрицательной.';
      if (!insuranceType || !['full', 'basic'].includes(insuranceType)) {
        newErrors.insuranceType = "Выберите тип страхования.";
      }
    }

    // Валидация Trade-In
    if (includeTradeIn) {
      const numTradeInValue = parseFloatOrDefault(tradeInValue, -1);
      if (numTradeInValue <= 0) newErrors.tradeInValue = 'Стоимость Trade-In должна быть положительной.';
      if (!newErrors.tradeInValue && numTradeInValue >= numCarPrice) {
        newErrors.tradeInValue = 'Стоимость Trade-In не может быть больше или равна стоимости нового автомобиля.';
      }
    }

    // Валидация досрочного погашения
    if (includeEarlyRepayment) {
      const numEarlyRepaymentAmount = parseFloatOrDefault(earlyRepaymentAmount, -1);
      const numEarlyRepaymentStartMonth = parseIntOrDefault(earlyRepaymentStartMonth, -1);
      const loanAmount = numCarPrice - numDownPayment;

      if (numEarlyRepaymentAmount <= 0) newErrors.earlyRepaymentAmount = 'Сумма должна быть положительной.';
      if (!newErrors.earlyRepaymentAmount && numEarlyRepaymentAmount >= loanAmount) {
        newErrors.earlyRepaymentAmount = 'Сумма досрочного погашения не может быть больше или равна сумме кредита.';
      }
      if (!isInteger(earlyRepaymentStartMonth) || numEarlyRepaymentStartMonth <= 0) {
        newErrors.earlyRepaymentStartMonth = 'Введите целый положительный номер месяца.';
      }
      if (!newErrors.loanTerm && !newErrors.earlyRepaymentStartMonth && numEarlyRepaymentStartMonth > numLoanTermMonths) {
        newErrors.earlyRepaymentStartMonth = `Начало (${numEarlyRepaymentStartMonth}) не может быть позже срока (${numLoanTermMonths} мес.).`;
      }
      if (!earlyRepaymentPeriod) newErrors.earlyRepaymentPeriod = "Выберите периодичность.";
      if (!earlyRepaymentType) newErrors.earlyRepaymentType = "Выберите цель погашения.";
    }

    // Валидация дополнительных сборов
    if (includeAdditionalFees) {
      const numRegistrationFee = parseFloatOrDefault(registrationFee, -1);
      const numAdditionalServices = parseFloatOrDefault(additionalServices, -1);
      
      if (numRegistrationFee < 0) newErrors.registrationFee = 'Значение не может быть отрицательным.';
      if (numAdditionalServices < 0) newErrors.additionalServices = 'Значение не может быть отрицательным.';
    }

    // Валидация типа платежа
    if (!paymentType || !['annuity', 'differentiated'].includes(paymentType)) {
      newErrors.paymentType = "Выберите корректный тип платежа.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет аннуитетного платежа
  const calculateAnnuityPayment = (principal, termMonths, annualRate) => {
    if (principal <= 0 || termMonths <= 0) return 0;
    const monthlyRate = annualRate / 12 / 100;

    if (monthlyRate === 0) {
      return principal / termMonths;
    }

    const factor = Math.pow(1 + monthlyRate, termMonths);
    if (!isFinite(factor) || factor === 1) {
      return principal / termMonths;
    }
    return principal * monthlyRate * factor / (factor - 1);
  };

  // Функция расчета графика платежей
  const calculatePaymentSchedule = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numCarPrice = parseFloatOrDefault(formData.carPrice);
        const numDownPayment = parseFloatOrDefault(formData.downPayment);
        const initialLoanAmount = numCarPrice - numDownPayment;

        if (initialLoanAmount <= 0) {
          setErrors({ general: "Сумма кредита должна быть положительной. Первоначальный взнос превышает стоимость автомобиля." });
          setIsCalculating(false);
          return;
        }

        const initialLoanTermYears = parseFloatOrDefault(formData.loanTerm);
        const initialLoanTermMonths = Math.round(initialLoanTermYears * 12);
        const initialAnnualRate = parseFloatOrDefault(formData.interestRate);
        const paymentType = formData.paymentType;
        const startDateObj = new Date(formData.startDate);

        // Учет страховки
        const includeInsurance = formData.includeInsurance;
        const insuranceCost = includeInsurance ? parseFloatOrDefault(formData.insuranceCost, 0) : 0;
        const insurancePerMonth = insuranceCost / 12; // Равномерное распределение стоимости страховки

        // Учет дополнительных сборов
        const includeAdditionalFees = formData.includeAdditionalFees;
        const registrationFee = includeAdditionalFees ? parseFloatOrDefault(formData.registrationFee, 0) : 0;
        const additionalServices = includeAdditionalFees ? parseFloatOrDefault(formData.additionalServices, 0) : 0;
        const initialFees = registrationFee + additionalServices;

        // Учет Trade-In
        const includeTradeIn = formData.includeTradeIn;
        const tradeInValue = includeTradeIn ? parseFloatOrDefault(formData.tradeInValue, 0) : 0;
        
        // Если включен Trade-In, уменьшаем сумму кредита
        let effectiveLoanAmount = initialLoanAmount;
        if (includeTradeIn && tradeInValue > 0) {
          effectiveLoanAmount = Math.max(0, initialLoanAmount - tradeInValue);
        }

        // Учет досрочного погашения
        const includeEarlyRepayment = formData.includeEarlyRepayment;
        const earlyRepaymentType = formData.earlyRepaymentType;
        const earlyRepaymentAmount = includeEarlyRepayment ? parseFloatOrDefault(formData.earlyRepaymentAmount, 0) : 0;
        const earlyRepaymentPeriod = formData.earlyRepaymentPeriod;
        const earlyRepaymentStartMonth = includeEarlyRepayment ? parseIntOrDefault(formData.earlyRepaymentStartMonth, 1) : 1;

        // --- Инициализируем переменные для расчета ---
        let currentLoanBalance = effectiveLoanAmount;
        let remainingTermMonthsForCalc = initialLoanTermMonths;
        let totalPrincipalPaidScheduled = 0;
        let totalInterestPaid = 0;
        let totalInsurancePaid = 0;
        let totalAdditionalFeesPaid = initialFees;
        let totalEarlyRepaymentPaid = 0;

        const calculatedSchedule = [];
        let currentMonth = 0;

        // Рассчитываем начальный аннуитетный платеж (если применимо)
        let fixedAnnuityPayment = (paymentType === 'annuity')
            ? calculateAnnuityPayment(effectiveLoanAmount, initialLoanTermMonths, initialAnnualRate)
            : 0;

        // Определяем частоту досрочного погашения
        let earlyRepaymentFrequencyMonths = 0;
        if (includeEarlyRepayment && earlyRepaymentAmount > 0) {
            switch (earlyRepaymentPeriod) {
                case 'monthly': earlyRepaymentFrequencyMonths = 1; break;
                case 'quarterly': earlyRepaymentFrequencyMonths = 3; break;
                case 'semi_annually': earlyRepaymentFrequencyMonths = 6; break;
                case 'annually': earlyRepaymentFrequencyMonths = 12; break;
                case 'once': earlyRepaymentFrequencyMonths = -1; break;
                default: earlyRepaymentFrequencyMonths = 0;
            }
        }

        // --- Цикл расчета графика платежей ---
        const maxIterations = initialLoanTermMonths * 2; // Некоторый запас итераций

        while (currentLoanBalance > 0.005 && currentMonth < maxIterations) {
            currentMonth++;
            const paymentDate = new Date(startDateObj);
            paymentDate.setMonth(startDateObj.getMonth() + currentMonth - 1);

            // Рассчитываем ежемесячные проценты
            const monthlyRate = initialAnnualRate / 12 / 100;
            let interestPayment = currentLoanBalance * monthlyRate;
            interestPayment = Math.max(0, interestPayment);

            // Рассчитываем плановое погашение основного долга и ежемесячный платеж
            let principalPaymentScheduled = 0;
            let monthlyPaymentBase = 0;

            if (paymentType === 'annuity') {
                monthlyPaymentBase = fixedAnnuityPayment;
                principalPaymentScheduled = monthlyPaymentBase - interestPayment;

                if (currentLoanBalance + interestPayment <= monthlyPaymentBase + 0.005) {
                    principalPaymentScheduled = currentLoanBalance;
                    monthlyPaymentBase = currentLoanBalance + interestPayment;
                }
            } else { // Дифференцированный
                const basePrincipalPayment = effectiveLoanAmount / initialLoanTermMonths;
                principalPaymentScheduled = basePrincipalPayment;

                if (currentLoanBalance <= basePrincipalPayment + 0.005) {
                    principalPaymentScheduled = currentLoanBalance;
                }
                monthlyPaymentBase = principalPaymentScheduled + interestPayment;
            }
            principalPaymentScheduled = Math.max(0, principalPaymentScheduled);

            // Рассчитываем ежемесячную страховку
            let monthlyInsurancePayment = 0;
            if (includeInsurance && insurancePerMonth > 0) {
                // В первый год полное значение, далее уменьшается на 10% ежегодно
                const insuranceYear = Math.floor((currentMonth - 1) / 12);
                const insuranceDiscount = Math.max(0, Math.min(0.5, insuranceYear * 0.1)); // Max 50% скидки
                monthlyInsurancePayment = insurancePerMonth * (1 - insuranceDiscount);
                totalInsurancePaid += monthlyInsurancePayment;
            }

            // Рассчитываем досрочное погашение
            let currentEarlyRepayment = 0;
            if (includeEarlyRepayment && earlyRepaymentAmount > 0 && currentMonth >= earlyRepaymentStartMonth && currentLoanBalance > 0) {
                const isRepaymentMonth =
                    (earlyRepaymentFrequencyMonths === -1 && currentMonth === earlyRepaymentStartMonth) ||
                    (earlyRepaymentFrequencyMonths > 0 && (currentMonth - earlyRepaymentStartMonth) % earlyRepaymentFrequencyMonths === 0);

                if (isRepaymentMonth) {
                    const maxEarlyRepayment = Math.max(0, currentLoanBalance - principalPaymentScheduled);
                    currentEarlyRepayment = Math.min(earlyRepaymentAmount, maxEarlyRepayment);
                }
            }

            // Рассчитываем общую сумму платежей за месяц
            const totalPaymentThisMonth = monthlyPaymentBase + monthlyInsurancePayment + currentEarlyRepayment;

            // Обновляем итоговые суммы
            totalPrincipalPaidScheduled += principalPaymentScheduled;
            totalInterestPaid += interestPayment;
            totalEarlyRepaymentPaid += currentEarlyRepayment;

            // Обновляем остаток кредита
            const startingBalance = currentLoanBalance;
            currentLoanBalance -= principalPaymentScheduled;
            currentLoanBalance -= currentEarlyRepayment;
            currentLoanBalance = Math.max(0, currentLoanBalance);

            // Пересчитываем аннуитетный платеж после досрочного погашения
            if (currentEarlyRepayment > 0 && paymentType === 'annuity' && currentLoanBalance > 0.005) {
                remainingTermMonthsForCalc = initialLoanTermMonths - currentMonth;
                if (remainingTermMonthsForCalc > 0) {
                    if (earlyRepaymentType === 'payment') {
                        // Для уменьшения платежа - пересчитываем аннуитет с новым остатком и тем же сроком
                        fixedAnnuityPayment = calculateAnnuityPayment(currentLoanBalance, remainingTermMonthsForCalc, initialAnnualRate);
                    } else {
                        // Для уменьшения срока - оставляем тот же платеж
                    }
                } else {
                    fixedAnnuityPayment = 0;
                }
            }

            // Добавляем запись в график платежей
            calculatedSchedule.push({
                month: currentMonth,
                date: paymentDate,
                payment: monthlyPaymentBase,
                principalPayment: principalPaymentScheduled,
                interestPayment: interestPayment,
                startingBalance: startingBalance,
                remainingBalance: currentLoanBalance,
                earlyRepayment: currentEarlyRepayment,
                insurancePayment: monthlyInsurancePayment,
                totalPayment: totalPaymentThisMonth
            });
        }

        if (currentMonth >= maxIterations && currentLoanBalance > 0.005) {
            console.warn("Расчет прерван из-за превышения максимального количества итераций.");
            setErrors({ general: "Расчет прерван из-за превышения максимального количества итераций. Проверьте параметры, особенно срок и досрочное погашение." });
            setResults(null);
            setSchedule([]);
            setIsCalculating(false);
            return;
        }

        const effectiveTermMonths = calculatedSchedule.length;

        // --- Готовим итоговые результаты ---
        const totalPrincipalPaid = totalPrincipalPaidScheduled + totalEarlyRepaymentPaid;
        const totalAdditionalCosts = totalInterestPaid + totalInsurancePaid + totalAdditionalFeesPaid;
        const totalPaymentOverall = effectiveLoanAmount + totalAdditionalCosts;
        const actualMonthlyPayment = effectiveTermMonths > 0 ? calculatedSchedule.slice(0, Math.min(12, effectiveTermMonths)).reduce((sum, item) => sum + item.payment, 0) / Math.min(12, effectiveTermMonths) : 0;
        const averageInsurancePayment = effectiveTermMonths > 0 ? totalInsurancePaid / effectiveTermMonths : 0;
        
        const finalResults = {
            carPrice: numCarPrice,
            downPayment: numDownPayment,
            downPaymentPercent: (numDownPayment / numCarPrice) * 100,
            loanAmount: effectiveLoanAmount,
            initialLoanAmount: initialLoanAmount,
            tradeInDiscount: tradeInValue,
            interestRate: initialAnnualRate,
            loanTermYears: initialLoanTermYears,
            loanTermMonths: initialLoanTermMonths,
            paymentType: paymentType,

            effectiveTermMonths: effectiveTermMonths,
            firstMonthlyPayment: calculatedSchedule.length > 0 ? calculatedSchedule[0].payment : 0,
            averageMonthlyPayment: actualMonthlyPayment,
            lastMonthlyPayment: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].payment : 0,
            
            totalPrincipalPaid: totalPrincipalPaid,
            totalInterest: totalInterestPaid,
            totalInsurance: totalInsurancePaid,
            averageInsurancePayment: averageInsurancePayment,
            totalAdditionalFees: totalAdditionalFeesPaid,
            
            totalEarlyRepayment: totalEarlyRepaymentPaid,
            totalAdditionalCosts: totalAdditionalCosts,
            totalPayment: totalPaymentOverall,
            overpaymentRatio: effectiveLoanAmount > 0 ? totalAdditionalCosts / effectiveLoanAmount : 0,
            
            loanStartDate: startDateObj,
            loanEndDate: calculatedSchedule.length > 0 ? calculatedSchedule[calculatedSchedule.length - 1].date : null,
            
            scheduleShort: calculatedSchedule.slice(0, 12) // Первые 12 месяцев для предпросмотра
        };

        setResults(finalResults);
        setSchedule(calculatedSchedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setIsCalculating]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      carPrice: '1500000',
      downPayment: '300000',
      downPaymentPercent: '20',
      loanTerm: '5',
      interestRate: '9.5',
      paymentType: 'annuity',
      startDate: new Date().toISOString().split('T')[0],
      carType: 'new',
      carAge: '0',
      includeInsurance: true,
      insuranceType: 'full',
      insuranceCost: '60000',
      includeAdditionalFees: false,
      registrationFee: '2000',
      additionalServices: '0',
      includeEarlyRepayment: false,
      earlyRepaymentType: 'reduction',
      earlyRepaymentAmount: '100000',
      earlyRepaymentPeriod: 'quarterly',
      earlyRepaymentStartMonth: '12',
      includeTradeIn: false,
      tradeInValue: '400000',
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
    setSaveLoanName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика платежей в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Месяц', 'Дата', 'Платеж (Осн. долг + %)', 'Основной долг', 'Проценты'];
    if (results.totalInsurance > 0) headers.push('Страховка');
    if (results.totalEarlyRepayment > 0) headers.push('Досрочное погашение');
    headers.push('Общий платеж за месяц', 'Остаток долга');

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = schedule.map(item => {
      const rowData = [
        item.month,
        item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        formatCsvNumber(item.payment),
        formatCsvNumber(item.principalPayment),
        formatCsvNumber(item.interestPayment),
      ];
      if (results.totalInsurance > 0) rowData.push(formatCsvNumber(item.insurancePayment));
      if (results.totalEarlyRepayment > 0) rowData.push(formatCsvNumber(item.earlyRepayment));
      rowData.push(
        formatCsvNumber(item.totalPayment),
        formatCsvNumber(item.remainingBalance)
      );
      return rowData.join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(schedule.reduce((sum, item) => sum + item.payment, 0)),
      formatCsvNumber(results.totalPrincipalPaid),
      formatCsvNumber(results.totalInterest)
    ];
    if (results.totalInsurance > 0) totalRow.push(formatCsvNumber(results.totalInsurance));
    if (results.totalEarlyRepayment > 0) totalRow.push(formatCsvNumber(results.totalEarlyRepayment));
    totalRow.push(formatCsvNumber(results.totalPayment), '');
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Стоимость автомобиля:', formatCsvNumber(results.carPrice)],
      ['Первоначальный взнос:', formatCsvNumber(results.downPayment)],
      ['Сумма кредита:', formatCsvNumber(results.loanAmount)],
      ['Срок кредита:', `${results.loanTermMonths} мес.`],
      ['Процентная ставка:', `${results.interestRate}%`],
      ['Тип платежа:', formData.paymentType === 'annuity' ? 'Аннуитетный' : 'Дифференцированный'],
      ['Дата начала кредита:', new Date(formData.startDate).toLocaleDateString('ru-RU')],
      ['Переплата:', formatCsvNumber(results.totalAdditionalCosts)],
      ['Полная стоимость кредита:', formatCsvNumber(results.totalPayment)],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'автокредит_график_платежей.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения кредита
  const handleSaveLoan = () => {
    if (showSaveDialog) {
      const savedResult = saveAutoLoan(saveLoanName || null);
      if (savedResult) {
        alert(`Автокредит "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveLoanName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculatePaymentSchedule();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Параметры автокредита
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Параметры автомобиля */}
        <CarParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange}
          errors={errors} 
        />

        {/* Trade-In */}
        {formData.includeTradeIn && (
          <TradeInSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Досрочное погашение */}
        {formData.includeEarlyRepayment && (
          <EarlyRepaymentSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
            earlyRepaymentPeriods={earlyRepaymentPeriods || [
              { id: 'monthly', name: 'Ежемесячно' },
              { id: 'quarterly', name: 'Ежеквартально' },
              { id: 'semi_annually', name: 'Раз в полгода' },
              { id: 'annually', name: 'Ежегодно' },
              { id: 'once', name: 'Разово' }
            ]}
          />
        )}

        {/* Дополнительные сборы */}
        {formData.includeAdditionalFees && (
          <AdditionalFeesSection 
            formData={formData} 
            handleInputChange={handleInputChange} 
            errors={errors} 
          />
        )}

        {/* Диалог сохранения кредита */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveLoanName}
                onChange={(e) => setSaveLoanName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveLoan}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать автокредит'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveLoan}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;